/**
 * Hook para gerenciar Analytics
 * Seguindo Clean Architecture e React Hooks best practices
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { AnalyticsRepository } from '../../data/repositories/AnalyticsRepository';
import {
  DemographicsStats,
  EmotionStats,
  HourlyStats,
  SystemStatsComplete,
} from '../../domain/entities/Analytics';
import { environment } from '../../config/environment';

/**
 * Estado do hook
 */
interface UseAnalyticsState {
  systemStats: SystemStatsComplete | null;
  demographics: DemographicsStats | null;
  emotions: EmotionStats | null;
  hourlyStats: HourlyStats | null;
  loading: boolean;
  error: string | null;
  isRefreshing: boolean; // Flag para evitar múltiplas requisições simultâneas
}

/**
 * Parâmetros para filtros
 */
export interface AnalyticsFilters {
  camera_id?: string;
  camera_ids?: string;
  start_date?: string;
  end_date?: string;
  date?: string;
  period?: 'today' | 'week' | 'month' | 'custom';
}

/**
 * Hook para gerenciar dados de analytics
 * @param autoRefresh Habilitar atualização automática (padrão: true)
 * @param refreshInterval Intervalo de atualização em ms (padrão: do environment)
 */
export function useAnalytics(
  autoRefresh: boolean = true,
  refreshInterval: number = environment.refreshInterval
) {
  const [state, setState] = useState<UseAnalyticsState>({
    systemStats: null,
    demographics: null,
    emotions: null,
    hourlyStats: null,
    loading: true,
    error: null,
    isRefreshing: false,
  });

  const repository = useMemo(() => new AnalyticsRepository(), []);

  /**
   * Buscar estatísticas do sistema
   */
  const fetchSystemStats = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));
      const data = await repository.getSystemStats();
      setState((prev) => ({ ...prev, systemStats: data, loading: false, error: null }));
    } catch (error) {
      console.error('❌ Erro ao buscar estatísticas:', error);
      setState((prev) => ({ ...prev, loading: false }));
      // ⚠️ NÃO seta erro global - mantém dados antigos na tela
    }
  }, [repository]);

  /**
   * Buscar dados demográficos
   */
  const fetchDemographics = useCallback(
    async (filters?: AnalyticsFilters) => {
      try {
        setState((prev) => ({ ...prev, loading: true }));
        const data = await repository.getDemographics(filters);
        setState((prev) => ({ ...prev, demographics: data, loading: false, error: null }));
      } catch (error) {
        console.error('❌ Erro ao buscar dados demográficos:', error);
        setState((prev) => ({ ...prev, loading: false }));
        // ⚠️ NÃO seta erro global - mantém dados antigos na tela
      }
    },
    [repository]
  );

  /**
   * 🎭 Buscar dados de emoções (feature premium — flag da organização)
   */
  const fetchEmotions = useCallback(
    async (filters?: AnalyticsFilters) => {
      try {
        const data = await repository.getEmotions(filters);
        setState((prev) => ({ ...prev, emotions: data }));
      } catch (error) {
        console.error('❌ Erro ao buscar dados de emoções:', error);
        // ⚠️ NÃO seta erro global - mantém dados antigos na tela
      }
    },
    [repository]
  );

  /**
   * Buscar dados por hora
   */
  const fetchHourlyStats = useCallback(
    async (filters?: AnalyticsFilters) => {
      try {
        setState((prev) => ({ ...prev, loading: true }));
        const data = await repository.getHourlyStats(filters);
        setState((prev) => ({ ...prev, hourlyStats: data, loading: false, error: null }));
      } catch (error) {
        console.error('❌ Erro ao buscar dados por hora:', error);
        setState((prev) => ({ ...prev, loading: false }));
        // ⚠️ NÃO seta erro global - mantém dados antigos na tela
      }
    },
    [repository]
  );

  /**
   * Carregar todos os dados de uma vez
   */
  const fetchAll = useCallback(
    async (filters?: AnalyticsFilters) => {
      await Promise.all([
        fetchSystemStats(),
        fetchDemographics(filters),
        fetchEmotions(filters),
        fetchHourlyStats(filters),
      ]);
    },
    [fetchSystemStats, fetchDemographics, fetchEmotions, fetchHourlyStats]
  );

  /**
   * Efeito para carregamento inicial e auto-refresh
   */
  useEffect(() => {
    fetchAll();

    if (autoRefresh) {
      const interval = setInterval(async () => {
        // ⚠️ Evitar múltiplas requisições simultâneas
        if (state.isRefreshing) {
          console.warn('⏭️ Auto-refresh pulado: requisição anterior ainda em andamento');
          return;
        }

        setState(prev => ({ ...prev, isRefreshing: true }));
        try {
          await fetchAll();
        } catch (err) {
          console.error('❌ Erro no auto-refresh:', err);
          // ⚠️ NÃO seta erro global - mantém dados antigos
        } finally {
          setState(prev => ({ ...prev, isRefreshing: false }));
        }
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchAll, state.isRefreshing]);

  return {
    ...state,
    fetchSystemStats,
    fetchDemographics,
    fetchEmotions,
    fetchHourlyStats,
    fetchAll,
    refresh: fetchAll,
  };
}
